/*
 * dashcam/store.js — where the recorder's bytes live.
 *
 *   openStore({workerUrl}) → a store backed by the storage worker (the
 *     origin's private file system, sync access handles — survives a crash),
 *     or, when the browser has no sync access handles, a MemoryStore with the
 *     same surface (the page then says the loop lives only while it is open,
 *     and keeps it short).
 *
 * Surface (all names are plain file names inside «dashcam/»):
 *   kind                         'disk' | 'memory'
 *   create(name)                 → Promise
 *   append(name, u8)             fire-and-forget (the buffer is transferred)
 *   writeAt(name, u8, at)        fire-and-forget
 *   close(name)                  → Promise<size>
 *   flush()                      → Promise
 *   read(name, off, len)         → Promise<Uint8Array>
 *   headers(name)                → Promise<{hdr:Uint8Array, offs:Float64Array, torn}>
 *   writeFile(name, u8|string)   → Promise
 *   readText(name)               → Promise<string|null>
 *   remove(name)                 → Promise
 *   list()                       → Promise<[{name,size}]>
 *   getFile(name, type)          → Promise<Blob>   (for download / playback)
 *   onError                      (err) — a write the disk refused
 */

import { HDR } from './ring.js';

class WorkerStore {
    constructor(worker, dir) {
        this.kind = 'disk';
        this.worker = worker;
        this.dir = dir || 'dashcam';
        this.pending = new Map();
        this.nextId = 1;
        this.onError = null;
        this.dead = null;
        // a file whose write the disk refused: its close() rejects with that error,
        // so a muxed file that lost bytes can never be reported as saved
        this.failed = new Map();
        worker.onmessage = (ev) => {
            const m = ev.data || {};
            if (m.op === 'error') {
                const e = new Error(m.message || 'write failed'); e.name = m.errName || 'Error'; e.file = m.name; e.at = m.at;
                this.failed.set(m.name, e);
                if (this.onError) { try { this.onError(e); } catch (_) {} }
                return;
            }
            const p = this.pending.get(m.id);
            if (!p) return;
            this.pending.delete(m.id);
            if (m.ok === false) { const e = new Error(m.message || m.errName || 'store'); e.name = m.errName || 'Error'; p.reject(e); }
            else p.resolve(m);
        };
        worker.onerror = (ev) => {
            const e = new Error((ev && ev.message) || 'storage worker failed');
            this.dead = e;
            for (const [, p] of this.pending) p.reject(e);
            this.pending.clear();
            if (this.onError) { try { this.onError(e); } catch (_) {} }
        };
    }
    req(msg, transfer) {
        if (this.dead) return Promise.reject(this.dead);
        const id = this.nextId++;
        return new Promise((resolve, reject) => {
            this.pending.set(id, { resolve, reject });
            msg.id = id;
            try { this.worker.postMessage(msg, transfer || []); } catch (e) { this.pending.delete(id); reject(e); }
        });
    }
    create(name) { this.failed.delete(name); return this.req({ op: 'create', name }).then(() => undefined); }
    append(name, u8) {
        const ab = own(u8);
        try { this.worker.postMessage({ op: 'append', name, data: ab }, [ab]); } catch (_) {}
    }
    writeAt(name, u8, at) {
        // always a copy: the muxer owns `u8` and may hand over its whole buffer,
        // which a transfer would detach under it
        const ab = (u8 instanceof Uint8Array ? u8 : new Uint8Array(u8)).slice().buffer;
        try { this.worker.postMessage({ op: 'writeAt', name, data: ab, at }, [ab]); } catch (_) {}
    }
    close(name) {
        return this.req({ op: 'close', name }).then((r) => {
            const e = this.failed.get(name);
            if (e) { this.failed.delete(name); throw e; }
            return r.size;
        });
    }
    flush() { return this.req({ op: 'flush' }).then(() => undefined); }
    read(name, off, len) { return this.req({ op: 'read', name, off, len }).then(r => new Uint8Array(r.data)); }
    headers(name) {
        return this.req({ op: 'headers', name }).then(r => ({ hdr: new Uint8Array(r.hdr), offs: new Float64Array(r.offs), torn: !!r.torn, size: r.size }));
    }
    writeFile(name, data) {
        if (typeof data === 'string') return this.req({ op: 'writeFile', name, data }).then(() => undefined);
        const ab = own(data);
        return this.req({ op: 'writeFile', name, data: ab }, [ab]).then(() => undefined);
    }
    readText(name) { return this.req({ op: 'readText', name }).then(r => r.text); }
    remove(name) { return this.req({ op: 'remove', name }).then(() => undefined); }
    list() { return this.req({ op: 'list' }).then(r => r.files || []); }
    async getFile(name, type) {
        const root = await navigator.storage.getDirectory();
        const dir = await root.getDirectoryHandle(this.dir);
        const fh = await dir.getFileHandle(name);
        const f = await fh.getFile();
        return type && f.type !== type ? new Blob([f], { type }) : f;
    }
}

/** A copy that owns its whole buffer — safe to transfer, never a view into a buffer someone reuses. */
function own(u8) {
    if (!(u8 instanceof Uint8Array)) u8 = new Uint8Array(u8);
    if (u8.byteOffset === 0 && u8.byteLength === u8.buffer.byteLength) return u8.buffer;
    return u8.slice().buffer;
}

/**
 * The same surface in memory. Used where sync access handles do not exist,
 * and by the unit tests. Appended files are lists of parts (no copying of the
 * whole file per append); positional writes are materialised on close.
 */
export class MemoryStore {
    constructor() { this.kind = 'memory'; this.files = new Map(); this.onError = null; }
    _f(name) { const f = this.files.get(name); if (!f) { const e = new Error('not found: ' + name); e.name = 'NotFoundError'; throw e; } return f; }
    async create(name) { this.files.set(name, { parts: [], size: 0, writes: null }); }
    append(name, u8) {
        const f = this.files.get(name); if (!f) return;
        const c = new Uint8Array(u8); f.parts.push(c); f.size += c.byteLength;
    }
    writeAt(name, u8, at) {
        const f = this.files.get(name); if (!f) return;
        if (!f.writes) f.writes = [];
        const c = new Uint8Array(u8); f.writes.push({ at, c });
        if (at + c.byteLength > f.size) f.size = at + c.byteLength;
    }
    _bytes(f) {
        if (f.writes) {
            const out = new Uint8Array(f.size);
            let p = 0;
            for (const c of f.parts) { out.set(c, p); p += c.byteLength; }
            for (const w of f.writes) out.set(w.c, w.at);
            f.parts = [out]; f.writes = null;
        } else if (f.parts.length > 1) {
            const out = new Uint8Array(f.size);
            let p = 0;
            for (const c of f.parts) { out.set(c, p); p += c.byteLength; }
            f.parts = [out];
        }
        return f.parts[0] || new Uint8Array(0);
    }
    async close(name) { const f = this.files.get(name); if (!f) return -1; this._bytes(f); return f.size; }
    async flush() {}
    async read(name, off, len) {
        const f = this._f(name);
        // read without collapsing a growing segment into one buffer on every call
        if (!f.writes && f.parts.length > 1) {
            const out = new Uint8Array(Math.max(0, Math.min(len, f.size - off)));
            let p = 0, w = 0;
            for (const c of f.parts) {
                const a = Math.max(off, p), b = Math.min(off + out.byteLength, p + c.byteLength);
                if (b > a) { out.set(c.subarray(a - p, b - p), w); w += b - a; }
                p += c.byteLength;
                if (p >= off + out.byteLength) break;
            }
            return out;
        }
        const b = this._bytes(f);
        return b.slice(off, Math.min(b.byteLength, off + len));
    }
    async headers(name) {
        const f = this._f(name);
        const b = this._bytes(f);
        const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
        const offs = [];
        let at = 0, torn = false;
        while (at < b.byteLength) {
            if (at + HDR > b.byteLength || b[at] !== 0xDC) { torn = true; break; }
            const size = dv.getUint32(at + 16, true);
            if (at + HDR + size > b.byteLength) { torn = true; break; }
            offs.push(at); at += HDR + size;
        }
        const hdr = new Uint8Array(offs.length * HDR);
        offs.forEach((o, i) => hdr.set(b.subarray(o, o + HDR), i * HDR));
        return { hdr, offs: new Float64Array(offs), torn, size: b.byteLength };
    }
    async writeFile(name, data) {
        const c = typeof data === 'string' ? new TextEncoder().encode(data) : new Uint8Array(data);
        this.files.set(name, { parts: [c], size: c.byteLength, writes: null });
    }
    async readText(name) { const f = this.files.get(name); return f ? new TextDecoder().decode(this._bytes(f)) : null; }
    async remove(name) { this.files.delete(name); }
    async list() { return [...this.files.entries()].map(([name, f]) => ({ name, size: f.size })); }
    async getFile(name, type) { const f = this._f(name); return new Blob([this._bytes(f)], { type: type || '' }); }
}

/**
 * The storage worker when the browser can give it sync access handles, a
 * MemoryStore otherwise. `forceMemory` — for an origin whose private file
 * system itself lives in memory (an incognito window): a "disk" there is RAM
 * anyway, so the page should size the loop as if it were. `dir` — the folder of
 * the private file system the worker writes in (default «dashcam»).
 */
export async function openStore({ workerUrl, forceMemory, dir } = {}) {
    if (forceMemory || typeof Worker !== 'function' || !workerUrl
        || !(typeof navigator !== 'undefined' && navigator.storage && typeof navigator.storage.getDirectory === 'function')) {
        return new MemoryStore();
    }
    let w;
    try { w = new Worker(workerUrl); } catch (_) { return new MemoryStore(); }
    const st = new WorkerStore(w, dir);
    try {
        const r = await Promise.race([
            st.req(dir ? { op: 'init', dir } : { op: 'init' }),
            // the worker script is a network fetch: on a slow connection it takes seconds, and
            // giving up early would put a whole drive in memory — wait long, then fall back
            new Promise((_, rej) => setTimeout(() => rej(new Error('storage worker timeout')), 45000)),
        ]);
        if (!r.sync) { w.terminate(); return new MemoryStore(); }
        return st;
    } catch (_) {
        try { w.terminate(); } catch (__) {}
        return new MemoryStore();
    }
}
